/**
 * Capability Service - React Query Integration
 * Handles all capability-related API communications
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import { Capability, CreateCapabilityInput, UpdateCapabilityInput } from '../types/admin';
import { AxiosError } from 'axios';
import { useNotification } from '../hooks/useNotification';

// ============================================
// API Response Types
// ============================================

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  meta?: PaginationMeta;
  message?: string;
  error?: string;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CapabilityListParams {
  page: number;
  limit: number;
  status?: 'active' | 'inactive' | 'all';
}

export interface CapabilityListResult {
  data: Capability[];
  meta: PaginationMeta;
}

// ============================================
// Query Keys
// ============================================

export const capabilityKeys = {
  all: ['capabilities'] as const,
  lists: () => [...capabilityKeys.all, 'list'] as const,
  list: (params: CapabilityListParams) => [...capabilityKeys.lists(), params] as const,
  details: () => [...capabilityKeys.all, 'detail'] as const,
  detail: (id: string) => [...capabilityKeys.details(), id] as const,
};

// ============================================
// API Functions
// ============================================

/**
 * Fetches capabilities from the backend
 */
const fetchCapabilities = async (params: CapabilityListParams): Promise<CapabilityListResult> => {
  try {
    const response = await axiosInstance.get<ApiResponse<Capability[]>>('/v1/admin/capabilities', {
      params: {
        page: params.page,
        limit: params.limit,
        status: params.status ?? 'all',
      },
    });

    const data = response.data.data || [];

    return {
      data,
      meta: response.data.meta ?? {
        total: data.length,
        page: params.page,
        limit: params.limit,
        totalPages: data.length > 0 ? 1 : 0,
      },
    };
  } catch (error) {
    console.error('Error fetching capabilities:', error);
    throw error;
  }
};

/**
 * Fetches a single capability by publicId
 */
const fetchCapability = async (publicId: string): Promise<Capability> => {
  try {
    const response = await axiosInstance.get<ApiResponse<Capability>>(
      `/v1/admin/capabilities/${publicId}`
    );
    return response.data.data as Capability;
  } catch (error) {
    console.error('Error fetching capability:', error);
    throw error;
  }
};

/**
 * Creates a new capability
 */
const createCapability = async (input: CreateCapabilityInput): Promise<Capability> => {
  try {
    const response = await axiosInstance.post<ApiResponse<Capability>>(
      '/v1/admin/capabilities',
      input
    );
    return response.data.data as Capability;
  } catch (error) {
    console.error('Error creating capability:', error);
    throw error;
  }
};

/**
 * Updates an existing capability
 */
const updateCapability = async ({
  publicId,
  ...input
}: UpdateCapabilityInput): Promise<Capability> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<Capability>>(
      `/v1/admin/capabilities/${publicId}`,
      input
    );
    return response.data.data as Capability;
  } catch (error) {
    console.error('Error updating capability:', error);
    throw error;
  }
};

/**
 * Deletes a capability
 */
const deleteCapability = async (publicId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/v1/admin/capabilities/${publicId}`);
  } catch (error) {
    console.error('Error deleting capability:', error);
    throw error;
  }
};

/**
 * Updates capability active status
 */
const updateCapabilityStatus = async ({
  publicId,
  isActive,
}: {
  publicId: string;
  isActive: boolean;
}): Promise<Capability> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<Capability>>(
      `/v1/admin/capabilities/${publicId}/status`,
      { isActive }
    );
    return response.data.data as Capability;
  } catch (error) {
    console.error('Error updating capability status:', error);
    throw error;
  }
};

// ============================================
// React Query Hooks
// ============================================

/**
 * Hook to fetch capabilities
 */
export const useCapabilities = (params: CapabilityListParams) => {
  return useQuery({
    queryKey: capabilityKeys.list(params),
    queryFn: () => fetchCapabilities(params),
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
  });
};

/**
 * Hook to fetch a single capability by publicId
 */
export const useCapability = (publicId: string) => {
  return useQuery({
    queryKey: capabilityKeys.detail(publicId),
    queryFn: () => fetchCapability(publicId),
    enabled: !!publicId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to create a new capability
 */
export const useCreateCapability = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: createCapability,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.lists() });
      notification.success('Capability created successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to create capability';
      notification.error(message);
      console.error('Error creating capability:', error);
    },
  });
};

/**
 * Hook to update an existing capability
 */
export const useUpdateCapability = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: updateCapability,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.lists() });
      notification.success('Capability updated successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to update capability';
      notification.error(message);
      console.error('Error updating capability:', error);
    },
  });
};

/**
 * Hook to delete a capability
 */
export const useDeleteCapability = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: deleteCapability,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.lists() });
      notification.success('Capability deleted successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to delete capability';
      notification.error(message);
      console.error('Error deleting capability:', error);
    },
  });
};

/**
 * Hook to toggle capability active status with optimistic updates
 */
export const useToggleCapabilityStatus = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: updateCapabilityStatus,
    // Optimistic update
    onMutate: async (variables: { publicId: string; isActive: boolean }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: capabilityKeys.lists() });

      // Snapshot previous value
      const previousCapabilityLists = queryClient.getQueriesData<CapabilityListResult>({
        queryKey: capabilityKeys.lists(),
      });

      // Optimistically update
      queryClient.setQueriesData<CapabilityListResult>(
        { queryKey: capabilityKeys.lists() },
        (previous) =>
          previous
            ? {
                ...previous,
                data: previous.data.map((cap) =>
                  cap.publicId === variables.publicId
                    ? { ...cap, isActive: variables.isActive }
                    : cap
                ),
              }
            : previous
      );

      return { previousCapabilityLists };
    },
    // Rollback on error
    onError: (error: AxiosError<ApiResponse>, _variables, context) => {
      context?.previousCapabilityLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      const message = error.response?.data?.error || 'Failed to update capability status';
      notification.error(message);
      console.error('Error toggling capability status:', error);
    },
    // Refetch on success
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.lists() });
      notification.success(
        `Capability ${variables.isActive ? 'activated' : 'deactivated'} successfully`
      );
    },
  });
};
